import { DEFAULT_INVOICE_TYPE } from '@/domain/rendition/Rendition'
import type { DiscountType } from '@/types/invoice'
import type { RenditionSummary } from '@/domain/rendition/Rendition'
import type { RenditionPreviewItemInput, RenditionPreviewResult } from '@/domain/rendition/RenditionRepository'
import type { Rendition, RenditionInvoice, RenditionLine, LineTax, AccountingSplit, SharedReceiptFound } from '@/domain/rendition/Rendition'
import type { Field, FormResponses } from '@/types/templates'
import type { RenditionFormData, RenditionDraftDetail } from '@/domain/rendition/RenditionRepository'
import type { InvoiceLine, InvoiceLineTaxDetail, GroupedInvoiceLine } from '@/types/invoice'
import { invoiceFinalTotal } from '@/application/rendition/accounting'
import { snapshotLines } from '@/application/rendition/draft'
import { lineChargedTotal, lineNet } from '@/application/rendition/lineAdapter'
import { customFieldAnswersFromApi } from '@/application/rendition/splitMapping'
import { roundTo, shareTipOf } from '@/application/rendition/sharedExpense'
import { flattenInvoiceLines } from '@/utils/invoiceLineConsolidation'
import { fieldPath } from '@/utils/formFieldKey'

// VERIFIED contract (finnecto-api). External serializer keys:
//  - line: `product` (source=item_description), `discount` (source=discounts), `withholdings` (required, can be []),
//    `tax_details` (source=tax_relations; each: tax_name, percentage, taxable_amount, tax_amount), `number`.
//  - split inside a line: `cost_center`, `account` (NOT chart_account), `percentage`, `amount` (REQUIRED).
// The backend RECOMPUTES the invoice totals from the persisted lines and IGNORES the payload's
// `total` / `tax_amount` / `retention_amount`: taxes come from lines[].tax_details, withholdings from
// lines[].withholdings. The `total`/`tax_amount`/`retention_amount` we still send are optimistic hints.
// A tax counts as "real" (worth sending) only when it carries a name. Guard against a missing name:
// a blank/partial tax row (e.g. a withholding the backend returns with a null `tax_name`) must be
// dropped here, not crash `.trim()` on undefined.
const named = (t: LineTax): boolean => !!t.taxName?.trim()

// Provider per invoice is mandatory on the backend (Invoice.provider is NOT NULL). Send the real id for an
// existing provider, OR `provider_name` to have the backend create it inline (type=PROVIDER, NOT_CREATED)
// in the same POST — never both, never the `0` sentinel. Sending neither (provider: null) makes the backend
// silently drop the invoice → an empty rendition (the bug this fixes); client-side validation prevents it.
function providerPayload(inv: RenditionInvoice): { provider: number } | { provider_name: string } | { provider: null } {
  if (inv.providerId != null && inv.providerId > 0) return { provider: inv.providerId }
  if (inv.providerName?.trim()) return { provider_name: inv.providerName.trim() }
  return { provider: null }
}

/**
 * The `number` each line is sent with. By position, except on a shared receipt: the api re-lays the owner's
 * part by `number` after replacing the lines, so a saved line keeps its own and a new one follows the highest.
 */
function lineNumbers(inv: RenditionInvoice): number[] {
  if (!inv.shares?.length) return inv.lines.map((_, idx) => idx + 1)
  let next = Math.max(0, ...inv.lines.map((line) => line.number ?? 0))
  return inv.lines.map((line) => line.number ?? ++next)
}

/**
 * The item's `shared` block, its lines by the same `number` as the item's lines. Only the owner sends
 * `people`. OMITTED on a receipt never shared: only `null` stops sharing, so no client unshares by accident.
 */
function sharedPayload(inv: RenditionInvoice, dec: number, country?: string | null) {
  if (!inv.shares?.length) return inv.sharedWhenLoaded ? { shared: null } : {}
  const numbers = lineNumbers(inv)
  return {
    shared: {
      ...(inv.shareOwnedByOther ? {} : {
        people: inv.shares.map((share) => ({ user_id: share.id })),
      }),
      lines: inv.lines.flatMap((line, idx) => ((line.shareTotal ?? 0) > 0 ? [{ number: numbers[idx], amount: roundTo(line.shareTotal as number, dec) }] : [])),
      tip_amount: shareTipOf(inv, dec, country),
    },
  }
}

/**
 * ONE invoice as the backend parses it (header + tip + the full `lines` set). Exported because the
 * reviewer's "Guardar" sends the same shape: the invoice is left exactly as posted, and since lines carry
 * no ids the set is REPLACED — which is what makes an omitted line a deletion.
 */
export function mapInvoice(inv: RenditionInvoice, decimalsFor: (currency: string) => number, country?: string | null) {
  const dec = decimalsFor(inv.currency)
  const numbers = lineNumbers(inv)
  return {
    // Only present when editing an existing invoice — its presence is what tells the backend's
    // resubmit branching to update in place instead of creating a new one (item without `id` = new).
    ...(inv.id ? { id: inv.id } : {}),
    // Associates an invoice that ALREADY exists in the society instead of creating one. A different key
    // from `id` above by design: `id` means "already belongs to this rendition, update in place", and on
    // create the backend ignores it and creates a duplicate — so reusing it here would be the silent
    // double-invoice bug. Once such a draft is reopened the invoice IS part of the rendition, and the
    // hydration returns it as `id` (this key is create-time only).
    ...(inv.existingInvoiceId ? { existing_invoice_id: inv.existingInvoiceId } : {}),
    number: inv.number,
    type: inv.invoiceType,
    // Always sent, blank included, so clearing the field survives a resubmit. Note the associate
    // branch deliberately ignores a blank instead of overwriting (finnecto-api#3400): there it would
    // mean "nobody retyped what the invoice already carried", not "erase it".
    description: inv.description ?? '',
    // `provider: <id>` (existing) or `provider_name: "<name>"` (new, backend creates it inline) — see helper.
    ...providerPayload(inv),
    emission_date: inv.emissionDate,
    // Renditions have a single "fecha de compra" sent in both fields; mirror emission when due is unset
    // so an untouched date never reaches the backend as a null due_date.
    due_date: inv.dueDate ?? inv.emissionDate ?? null,
    currency: inv.currency,
    // No `exchange_rate`: the policy owns the rate and the backend derives it (a client rate is rejected).
    category: inv.categoryId,
    subcategory: inv.subcategoryId,
    area: inv.areaId,
    // Links the uploaded TwilioInvoiceFile to this invoice at create time (create_rendition_lines).
    // Must be the file `id` (NOT twilio_filename — that path deletes the file from S3).
    // OMITTED (not null) when there is none: the detail doesn't always return `document_file_id`, so
    // re-saving a loaded draft would otherwise declare "this invoice has no file". The backend ignores the
    // key when updating an existing invoice (`_apply_invoice_update` preserves the TwilioInvoiceFile link),
    // but stating absence explicitly is one refactor away from unlinking the document. Same pattern as `id`.
    ...(inv.documentFileId != null ? { document_file_id: inv.documentFileId } : {}),
    ...(inv.duplicateOverride ? { duplicate_override: true } : {}),
    ...sharedPayload(inv, dec, country),
    // Tip charged on this document. The backend owns the field (Invoice.tip_amount) and adds it into the
    // total it recomputes, so it is the one amount we send that is NOT just an optimistic hint.
    ...(inv.tip ? { tip_amount: roundTo(inv.tip, dec) } : {}),
    // Not the receipt's global discount: the backend keeps the one on the saved invoice, so neither
    // `discount_type` nor `discount_total` travels here. Each line's `discount` goes in the unit the line was
    // hydrated with (a % or, on a VALUE receipt, an amount). `invoiceFinalTotal` below does subtract the
    // receipt's `discountTotal`.
    discount: 0,
    // Optimistic hints — the backend ignores these and recomputes from lines.
    tax_amount: 0,
    retention_amount: 0,
    total: roundTo(invoiceFinalTotal(inv, country), dec),
    lines: inv.lines.map((l, idx) => {
      const lineTaxes = l.taxes.filter((t) => t.taxTypeCode === 'TAX' && named(t))
      const roundedTax = (t: Pick<LineTax, 'taxName' | 'taxPercentage' | 'taxableAmount' | 'taxAmount' | 'fiscalCode'>) => ({
        tax_name: t.taxName, percentage: t.taxPercentage,
        taxable_amount: roundTo(t.taxableAmount, dec), tax_amount: roundTo(t.taxAmount, dec),
        ...(t.fiscalCode ? { tax_type_code: t.fiscalCode } : {}),
      })
      const splitBase = lineNet(l, inv.discountType)
      return {
        number: numbers[idx],
        product: l.itemName,
        // Product catalog link (same as payment requests / purchase orders): a real id links an existing
        // product; a new one (productId 0/unset) is omitted so the backend creates it from `product` (name).
        product_id: l.productId || undefined,
        product_code: l.productCode,
        description: l.description,
        unit_price: roundTo(l.unitPrice, 6),
        quantity: l.quantity,
        discount: l.discount || 0,
        total: roundTo(l.total, dec),
        withholdings: l.taxes.filter((t) => t.taxTypeCode === 'WITHHOLDING' && named(t)).map(roundedTax),
        tax_details: lineTaxes.map(roundedTax),
        // A split is complete platform-wide when it has a cost center OR an account — both are individually
        // optional (see accountingValidation.hasIncompleteSplits and the backend's mandatory-accounting
        // guard, which agree). OMIT the missing dimension rather than send `null`: SplitInputSerializer's
        // `account` is `required=False` WITHOUT `allow_null`, so an explicit `account: null` 400s ("may not
        // be null") — and an id-0 sentinel 400s too. A row with neither dimension is dropped.
        splits: l.splits
          .filter((s) => s.costCenterId != null || s.accountId != null)
          .map((s) => ({
            ...(s.costCenterId != null ? { cost_center: s.costCenterId } : {}),
            ...(s.accountId != null ? { account: s.accountId } : {}),
            percentage: s.percentage,
            amount: roundTo((splitBase * s.percentage) / 100, dec),
            custom_fields_answers: s.customFieldsAnswers ?? {},
          })),
      }
    }),
  }
}

export function buildRenditionFormData(
  rendition: Rendition,
  decimalsFor: (currency: string) => number = () => 0,
): FormData {
  const fd = new FormData()
  fd.append('request_type', 'RENDITION')
  fd.append('policy', String(rendition.policyId))
  // Always send `category` (empty string when unset) — the backend requires the key to be present even for
  // drafts (otherwise it 400s with "Categoría inválida"). Matches the payment-request form's draft behavior.
  fd.append('category', rendition.categoryId ? String(rendition.categoryId) : '')
  if (rendition.subcategoryId) fd.append('subcategory', String(rendition.subcategoryId))
  if (rendition.areaId) fd.append('area', String(rendition.areaId))
  // Request-level cost center (policy's `field_visibility.cost_center`). Same top-level key compras sends;
  // the backend turns it into a single 100% header Split (shared code, no RENDITION special-casing).
  if (rendition.costCenterId != null) fd.append('cost_center', String(rendition.costCenterId))
  if (rendition.description) fd.append('description', rendition.description)
  fd.append('form', JSON.stringify(rendition.formResponse))
  // Beneficiary (employee to reimburse). Shares the payment-request keys: `recipient` + `recipient_type`.
  // The bank keys are appended only when present (national only); `recipient_national_type` is always
  // 'NATIONAL' whenever a beneficiary is set. No client-side validation — the backend enforces completeness.
  if (rendition.beneficiaryId != null) {
    fd.append('recipient', String(rendition.beneficiaryId))
    fd.append('recipient_type', 'EMPLOYEE')
    fd.append('recipient_national_type', rendition.recipientNationalType || 'NATIONAL')
    if (rendition.recipientBank) fd.append('recipient_bank', rendition.recipientBank)
    if (rendition.recipientBankCode) fd.append('recipient_bank_code', rendition.recipientBankCode)
    if (rendition.recipientAccountType) fd.append('recipient_account_type', rendition.recipientAccountType)
    if (rendition.recipientAccountNumber) fd.append('recipient_account_number', rendition.recipientAccountNumber)
    if (rendition.recipientEmail) fd.append('recipient_email', rendition.recipientEmail)
    if (rendition.recipientTaxId) fd.append('recipient_tax_id', rendition.recipientTaxId)
  }
  // No request `total`: the backend derives it from the lines it persists (a client total is rejected).
  fd.append('items', JSON.stringify(rendition.invoices.map((inv) => mapInvoice(inv, decimalsFor, rendition.country))))
  return fd
}

// ---- decode direction: GET purchases/requests/<id>/ → draft-shaped domain objects. ----
// Mirrors mapInvoice/buildRenditionFormData above (same wire contract, opposite direction).
// Verified against `RequestView.get`'s RENDITION branch (purchases/views/requests.py:2607-2642) —
// the GET shape is line-level only (no header discount/tax_details), per the 2026-07-02 decision.

interface RawCatalogRef { id: number; name: string }
// Fields are nullable in practice — a line can carry a blank/partial tax row (see rawTaxToLineTax).
interface RawTaxDetail {
  tax_name: string | null
  // The fiscal code ("01" IVA, "06" ReteFuente…): `tax_type` on a tax row, `tax_type_code` on a withholding.
  tax_type?: string | null
  tax_type_code?: string | null
  percentage: number | null
  taxable_amount: number | null
  tax_amount: number | null
}
interface RawSplit {
  cost_center: { id: number; name: string; code?: string } | null
  account: { id: number; name: string; code?: string } | null
  amount: number
  percentage: number
  custom_field_answers?: { custom_field_id: number; value: string }[]
}
interface RawLine {
  // Absent on the lines of a receipt looked up to take a part of it: they belong to no rendition yet.
  id?: number
  number: number
  product: string
  product_id?: number | null
  product_code?: string | null
  description?: string
  unit_price: number
  quantity: number
  discount: number
  total: number
  decision?: string | null
  is_adjustment?: boolean
  tax_details: RawTaxDetail[]
  withholdings: RawTaxDetail[]
  splits: RawSplit[]
}
// `InvoiceShareServices.rendition_block`: null when the receipt is not shared. Amounts are the receipt's
// currency; `part` is the part claimed on THIS rendition line, `value` what each receipt line charges.
interface RawSharedBlock {
  // Both about whoever is looking, not about whose part this rendition holds.
  is_owner: boolean
  is_designated?: boolean
  // Whether the receipt's document can be corrected from THIS rendition.
  editable_in_rendition?: boolean
  people: Array<{
    user_id: number
    name: string
    image: string | null
    claimed_amount?: number | string | null
    status?: 'CLAIMED' | 'APPROVED' | 'RELEASED' | null
  }>
  part: { user_id: number; tip_amount: number | string; lines: Array<{ number: number; amount: number | string }> } | null
  available_by_line: Array<{ number: number; value: number | string; available: number | string }>
  tip_available?: number | string
  parts_by_line?: Array<{ number: number; parts: Array<{ user_id: number; name: string; amount: number | string; approved: boolean }> }>
}
export interface RawInvoiceItem {
  id: number
  number: string
  provider: RawCatalogRef | null
  category: RawCatalogRef | null
  subcategory: RawCatalogRef | null
  area: RawCatalogRef | null
  description?: string
  // Fiscal type the invoice was saved with (INVOICE / SALES_RECEIPT / …). Added to the RENDITION branch of
  // the retrieve in finnecto-api 0d7237423, for parity with the PAYMENT branch.
  type?: string | null
  emission_date: string
  due_date?: string | null
  currency: string | null
  total: number
  decision?: string | null
  exchange_rate?: number | null
  converted_total?: number | null
  tip_amount?: number | null
  discount_type?: DiscountType | null
  discount_total?: number | null
  document_file_id: number | null
  document_url: string | null
  lines: RawLine[]
  shared?: RawSharedBlock | null
}

/**
 * The employee this request reimburses, as the detail returns it. It sits in the main response dict —
 * NOT behind `?get_options=true` — which is the same block the payment request reads back.
 */
// Only the fields the hydration actually reads. `national_type` is deliberately NOT declared: renditions
// have no international branch (`buildDraftRendition` always sends 'NATIONAL'), so reading it back would
// suggest a choice that doesn't exist.
export interface RawRecipient {
  id: number
  name?: string
  national_id?: string | null
  email?: string | null
  bank?: string | null
  account_number?: string | null
  account_type?: string | null
}

/** Raw `summary` block of a RENDITION detail and of the preview response. */
export interface RawSummary {
  currency: string
  subtotal: number
  discount: number
  taxes: number
  withholdings: number
  tip: number
  total: number
}

export function mapSummary(raw: RawSummary): RenditionSummary {
  return {
    currency: raw.currency,
    subtotal: raw.subtotal ?? 0,
    discount: raw.discount ?? 0,
    taxes: raw.taxes ?? 0,
    withholdings: raw.withholdings ?? 0,
    tip: raw.tip ?? 0,
    total: raw.total ?? 0,
  }
}

/** Body of `POST purchases/requests/rendition-preview/` (snake_case mirror of RenditionPreviewItemInput). */
export function toPreviewBody(policyId: number, items: RenditionPreviewItemInput[]): Record<string, unknown> {
  return {
    policy: policyId,
    items: items.map((i) => ({
      currency: i.currency,
      ...(i.emissionDate ? { emission_date: i.emissionDate } : {}),
      total: i.total,
      net_total: i.netTotal,
      discount_total: i.discountTotal,
      taxes_total: i.taxesTotal,
      withholding_total: i.withholdingTotal,
      tip_amount: i.tipAmount,
      ...(i.manualExchangeRate != null ? { manual_exchange_rate: i.manualExchangeRate } : {}),
      ...(i.documentFileId != null ? { document_file_id: i.documentFileId } : {}),
      ...(i.shared ? { shared: { lines: i.shared.lines, tip_amount: i.shared.tipAmount } } : {}),
    })),
  }
}

export interface RawPreviewResult {
  summary: RawSummary
  items: Array<{ index: number; currency: string; exchange_rate: number; native_total: number; converted_total: number }>
}

export function mapPreviewResult(raw: RawPreviewResult): RenditionPreviewResult {
  return {
    summary: mapSummary(raw.summary),
    items: (raw.items ?? []).map((i) => ({
      index: i.index, currency: i.currency, exchangeRate: i.exchange_rate, nativeTotal: i.native_total, convertedTotal: i.converted_total,
    })),
  }
}

/** Raw shape of `GET purchases/requests/<id>/` for a RENDITION request. */
export interface RawRenditionDetail {
  description?: string
  category: RawCatalogRef | null
  subcategory: RawCatalogRef | null
  area: RawCatalogRef | null
  policy: { id: number; name: string; display_name?: string }
  default_currency: string
  // Request-level (header) accounting splits — where the backend surfaces the header cost center on read
  // (there is no `cost_center` scalar on the request; it's a single 100% Split). Distinct from each
  // invoice line's own `splits`. Only `cost_center.id` is read back for the request-level selector.
  splits?: Array<{ cost_center?: { id: number } | null }>
  items: RawInvoiceItem[]
  // Policy-currency totals the backend computed for this rendición (null for other request types).
  summary?: RawSummary | null
  // The backend returns each answered policy-form field in the full `Field` shape plus its `answer`.
  form_responses?: Array<Field & { answer?: unknown }>
  recipient?: RawRecipient | null
}

/**
 * Turns the backend's `form_responses` (Field[] with `answer`) into Vueform's flat model — keyed by
 * field label (dots stripped, matching buildVueformSchema), each value in the shape Vueform binds:
 * dates as `yyyy-mm-dd`, multi-selects as string[], everything else passed through. The parsed
 * `Field[]` is returned untouched for the review step (which reads each field's `answer` directly).
 */
function hydrateFormResponses(raw: RawRenditionDetail['form_responses']): { fields: Field[]; model: FormResponses } {
  const fields = (raw ?? []) as Field[]
  const model: FormResponses = {}
  fields.forEach((f) => {
    const key = fieldPath(f)
    const answer = (f as Field & { answer?: unknown }).answer
    if (!key || answer == null) return
    if (f.type === 'custom-datepicker' && typeof answer === 'string' && answer.includes('/')) {
      const [day, month, year] = answer.split('/') // dd/mm/yyyy → yyyy-mm-dd (RenditionPolicyForm coerces to a local Date)
      model[key] = `${year}-${month}-${day}`
    } else if (f.type === 'custom-multi-select') {
      model[key] = typeof answer === 'string' ? answer.split(', ') : (answer as string[])
    } else {
      model[key] = answer as FormResponses[string]
    }
  })
  return { fields, model }
}

function rawTaxToLineTax(t: RawTaxDetail, taxTypeCode: 'TAX' | 'WITHHOLDING'): LineTax {
  // The backend can return a partial tax row (null name/percentage/amounts) for a line — coerce to safe
  // defaults so downstream `.trim()`/arithmetic never sees null, and the unnamed row is later filtered out.
  const fiscalCode = t.tax_type_code || t.tax_type
  return {
    taxName: t.tax_name ?? '', taxPercentage: t.percentage ?? 0, taxableAmount: t.taxable_amount ?? 0, taxAmount: t.tax_amount ?? 0, taxTypeCode,
    ...(fiscalCode ? { fiscalCode } : {}),
  }
}

/**
 * A split picks its chart account (`account`) directly — same model as the rest of the platform's
 * splits — so the raw `account` maps straight across. `formData.accounts` only fills in the account
 * code for the "code | name" selector label when the API response omits it.
 *
 * Each dimension maps independently and a PARTIAL split (only a cost center, or only an account) is
 * KEPT — both are individually optional platform-wide (a split is complete with cost center OR account),
 * so it is a valid split the reviewer must see, not something to hide. An unset id stays `undefined`; the
 * payload OMITS that dimension (never `null` — the serializer's `account` rejects null — and never `0`).
 */
function mapSplitFromApi(s: RawSplit, formData?: RenditionFormData): AccountingSplit {
  const accountId = s.account?.id
  return {
    costCenterId: s.cost_center?.id,
    costCenterLabel: s.cost_center?.name ?? '',
    accountId,
    accountLabel: s.account?.name ?? '',
    // Prefer the code the API sent; fall back to formData so the selector still shows "code | name".
    accountCode: s.account?.code ?? (accountId != null ? formData?.accounts.find((a) => a.id === accountId)?.code : undefined),
    customFieldsAnswers: customFieldAnswersFromApi(s.custom_field_answers),
    percentage: s.percentage ?? 0,
  }
}

function mapLineFromApi(line: RawLine, formData?: RenditionFormData): RenditionLine {
  return {
    id: line.id,
    number: line.number,
    itemName: line.product ?? '',
    productId: line.product_id ?? undefined,
    productCode: line.product_code ?? undefined,
    description: line.description,
    unitPrice: line.unit_price ?? 0,
    quantity: line.quantity ?? 1,
    discount: line.discount ?? 0,
    total: line.total ?? 0,
    taxes: [
      ...(line.tax_details ?? []).map((t) => rawTaxToLineTax(t, 'TAX')),
      ...(line.withholdings ?? []).map((t) => rawTaxToLineTax(t, 'WITHHOLDING')),
    ],
    splits: (line.splits ?? []).map((s) => mapSplitFromApi(s, formData)),
    ...(line.is_adjustment ? { isAdjustment: true } : {}),
  }
}


/**
 * Puts a saved shared receipt back on the invoice: its people with this rendition's requester first, the part on
 * each line (its quantity derived from what the line charges), and what the others took as each line's cap.
 */
function applySharedBlock(inv: RenditionInvoice, shared: RawSharedBlock): void {
  const partUser = shared.part?.user_id
  const people = [...shared.people].sort((a, b) => Number(b.user_id === partUser) - Number(a.user_id === partUser))
  inv.shares = people.map((p) => ({
    id: p.user_id,
    name: p.name,
    image: p.image,
    // A released claim no longer holds anything, so it reads as no claim at all.
    ...(p.claimed_amount != null && p.status !== 'RELEASED'
      ? { claimedAmount: Number(p.claimed_amount), claimStatus: p.status === 'APPROVED' ? 'APPROVED' as const : 'CLAIMED' as const }
      : {}),
  }))
  inv.sharedWhenLoaded = true
  inv.shareCurrencyWhenLoaded = inv.currency
  // Someone else's receipt by whose part this rendition holds, not by who looks (a designated reviewer
  // corrects the owner's like any reviewer). The api lists the owner first.
  const ownerId = shared.people[0]?.user_id
  const ownedByOther = shared.part ? shared.part.user_id !== ownerId : !shared.is_owner && shared.is_designated !== false
  if (ownedByOther) inv.shareOwnedByOther = true
  if (shared.editable_in_rendition === false) inv.shareDocumentLocked = true
  if (shared.part) inv.shareTip = Number(shared.part.tip_amount) || 0
  if (shared.tip_available != null) inv.shareTipAvailable = Number(shared.tip_available)
  inv.lines.forEach((line, idx) => {
    const number = line.number ?? idx + 1
    const byLine = shared.available_by_line.find((l) => l.number === number)
    const value = Number(byLine?.value ?? 0)
    const unit = line.quantity ? value / line.quantity : 0
    if (shared.part) {
      const amount = Number(shared.part.lines.find((l) => l.number === number)?.amount ?? 0)
      line.shareTotal = amount
      line.shareQty = unit ? Math.round((amount / unit) * 1e4) / 1e4 : 0
    }
    const takenByOthers = value - Number(byLine?.available ?? value)
    if (unit && takenByOthers > 0) line.shareTakenQty = Math.round((takenByOthers / unit) * 1e4) / 1e4
    if (byLine) line.shareAvailable = Number(byLine.available)
    const parts = shared.parts_by_line?.find((l) => l.number === number)?.parts ?? []
    if (parts.length && value > 0) {
      line.sharePartsValue = value
      line.shareParts = parts.map((p) => ({
        userId: p.user_id,
        name: p.name,
        approved: p.approved,
        amount: Number(p.amount),
        fraction: Number(p.amount) / value,
        mine: p.user_id === partUser,
        owner: p.user_id === ownerId,
      }))
    }
  })
}

/** `GET purchases/invoice-shares/lookup/`: the lines and the share block only reach a designated person. */
export interface RawSharedReceipt {
  invoice_id: number
  owner: { user_id: number; name: string; image: string | null }
  request: { id: number; number: number | null } | null
  is_designated: boolean
  my_live_part?: { request_id: number; request_number: number | null } | null
  number?: string | null
  type?: string | null
  emission_date?: string | null
  currency?: string | null
  provider?: RawCatalogRef | null
  total?: number
  document_url?: string | null
  tip_amount?: number | null
  discount_type?: DiscountType | null
  discount_total?: number | null
  lines?: RawLine[]
  shared?: RawSharedBlock | null
}

export function sharedReceiptFromApi(raw: RawSharedReceipt): SharedReceiptFound {
  const declaration = {
    invoiceId: raw.invoice_id,
    requestId: raw.request?.id ?? null,
    requestLabel: raw.request ? `#${raw.request.number ?? raw.request.id}` : '',
    declaredBy: { id: raw.owner.user_id, name: raw.owner.name, image: raw.owner.image },
    includesMe: raw.is_designated,
    ...(raw.my_live_part
      ? { myLivePart: { requestId: raw.my_live_part.request_id, requestLabel: `#${raw.my_live_part.request_number ?? raw.my_live_part.request_id}` } }
      : {}),
  }
  if (!raw.is_designated || !raw.lines || !raw.shared) return { declaration }
  const receipt = { lines: raw.lines.map((line) => mapLineFromApi(line)) } as RenditionInvoice
  applySharedBlock(receipt, raw.shared)
  return {
    declaration,
    receipt: {
      lines: receipt.lines,
      shares: receipt.shares,
      tip: raw.tip_amount ?? undefined,
      nativeTotal: raw.total,
      ...(raw.document_url ? { documentUrl: raw.document_url } : {}),
      ...(raw.discount_type ? { discountType: raw.discount_type } : {}),
      ...(raw.discount_total ? { discountTotal: raw.discount_total } : {}),
      providerId: raw.provider?.id,
      providerName: raw.provider?.name,
      number: raw.number ?? undefined,
      emissionDate: raw.emission_date?.slice(0, 10),
      currency: raw.currency ?? undefined,
      invoiceType: raw.type ?? undefined,
    },
  }
}

/** Receipts read in the rendition item shape by another flow (the anticipo receipts) into draft invoices. */
export function hydrateRenditionInvoices(items: RawInvoiceItem[], formData: RenditionFormData): RenditionInvoice[] {
  return items.map((item) => mapInvoiceFromApi(item, formData))
}

function mapInvoiceFromApi(item: RawInvoiceItem, formData: RenditionFormData): RenditionInvoice {
  const inv = invoiceFromApi(item, formData)
  if (item.shared) applySharedBlock(inv, item.shared)
  return inv
}

function invoiceFromApi(item: RawInvoiceItem, formData: RenditionFormData): RenditionInvoice {
  const lines = (item.lines ?? []).map((l) => mapLineFromApi(l, formData))
  return {
    id: item.id,
    // undefined (not 0) when no provider is set — matches RenditionInvoice's no-sentinel convention.
    providerId: item.provider?.id,
    providerName: item.provider?.name ?? '',
    number: item.number ?? '',
    description: item.description ?? undefined,
    // Fall back to the shared default only when the detail omits it (older requests persisted before the
    // backend returned it), never to a literal of our own — that divergence is what made the selector show
    // "Boleta" and the payload save "Factura".
    invoiceType: item.type ?? DEFAULT_INVOICE_TYPE,
    // The backend returns these as full ISO datetimes ("2026-07-24T00:00:00Z"); the rest of the
    // rendition flow (toDate in the carousel, the payload) treats them as plain "YYYY-MM-DD" dates,
    // so normalize on ingestion — a raw datetime breaks `new Date(`${iso}T12:00:00`)` (Invalid Date).
    emissionDate: item.emission_date?.slice(0, 10) ?? new Date().toISOString().slice(0, 10),
    // Single "fecha de compra": fall back to emission so a loaded draft with a null due_date doesn't
    // reopen with the date field flagged as missing.
    dueDate: item.due_date?.slice(0, 10) ?? item.emission_date?.slice(0, 10) ?? undefined,
    currency: item.currency ?? 'CLP',
    // Rate, native and converted total as the backend resolved them for the saved invoice. Read-only display
    // data. `nativeTotal` is what a pool-backed receipt displays (invoiceDisplayTotal) and loadDraft runs no
    // preview to fill it, so without it the carousel header stays on "…" forever.
    nativeTotal: item.total,
    exchangeRate: item.exchange_rate ?? undefined,
    convertedTotal: item.converted_total ?? undefined,
    tip: item.tip_amount ?? undefined,
    ...(item.discount_type ? { discountType: item.discount_type } : {}),
    ...(item.discount_total ? { discountTotal: item.discount_total } : {}),
    // Invoice-level taxes/discount are not part of the RENDITION contract (line-level only) —
    // the invoice's own `total` (item.total) is already the backend-recalculated sum of its lines.
    taxes: [],
    categoryId: item.category?.id,
    categoryLabel: item.category?.name,
    subcategoryId: item.subcategory?.id,
    subcategoryLabel: item.subcategory?.name,
    areaId: item.area?.id,
    areaLabel: item.area?.name,
    lines,
    // A pool-backed receipt keeps the lines it was saved with, the ones the backend compares a resubmit against.
    ...(item.document_file_id != null ? { receivedLines: snapshotLines(lines) } : {}),
    documentFileId: item.document_file_id ?? undefined,
    documentUrl: item.document_url ?? undefined,
    analysisStatus: 'manual',
  }
}

// ---- decode direction: GET purchases/invoices/<id>/ → rendition lines (invoice association). ----
// Separate from the rendition-detail decode above: this reads the INVOICE detail contract (the
// `InvoiceLine` shape shared with the Facturas views), not the rendition request contract.

/**
 * Maps one line of an already-persisted invoice into a rendition line.
 *
 * `product_id` is nullable on the wire (invoices without a master product carry the free-text
 * description in `product`); it maps to `undefined` so the payload omits it and the backend resolves the
 * product from the name — exactly what `mapInvoice` already does for a hand-typed line.
 *
 * The accounting splits are deliberately NOT carried over: the rendition's own accounting assignment is
 * what the emitter fills in (and the policy may require a different shape), so an inherited split would
 * be a value nobody chose. Taxes and withholdings ARE carried — they are properties of the document.
 */
function invoiceLineToRenditionLine(line: InvoiceLine): RenditionLine {
  const toTax = (t: InvoiceLineTaxDetail, taxTypeCode: 'TAX' | 'WITHHOLDING'): LineTax => ({
    taxName: t.tax_name ?? '',
    taxPercentage: t.percentage ?? 0,
    taxableAmount: t.taxable_amount ?? 0,
    taxAmount: t.tax_amount ?? 0,
    taxTypeCode,
    ...(t.tax_type_code ? { fiscalCode: t.tax_type_code } : {}),
  })
  const unitPrice = line.unit_price ?? 0
  const quantity = line.quantity ?? 1
  const discount = line.discount ?? 0
  const renditionLine: RenditionLine = {
    // No `id`: these lines belong to the source invoice, and a rendition line `id` means "an existing
    // rendition line to update in place". Carrying it over would aim the update at the wrong record.
    itemName: line.product ?? '',
    number: line.number,
    productId: line.product_id ?? undefined,
    productCode: line.product_code ?? undefined,
    description: line.description,
    unitPrice,
    quantity,
    discount,
    total: 0,
    taxes: [
      ...(line.tax_details ?? []).map((t) => toTax(t, 'TAX')),
      ...(line.withholdings ?? []).map((w) => toTax(
        // `Withholding` names the tax in `tax_type`, while `InvoiceLineTaxDetail` uses `tax_name`.
        { ...w, tax_name: w.tax_type } as unknown as InvoiceLineTaxDetail,
        'WITHHOLDING',
      )),
    ],
    splits: [],
  }
  return { ...renditionLine, total: lineChargedTotal(renditionLine) }
}

/**
 * Decodes the lines of `GET purchases/invoices/<id>/` into rendition lines, collapsing consolidated
 * groups first: `lines` carries BOTH a materialized consolidated line and the member originals it
 * replaced, so mapping the array as-is double-counts every grouped amount.
 */
export function hydrateInvoiceLinesForRendition(raw: RawInvoiceDetail): RenditionLine[] {
  return flattenInvoiceLines(raw.lines ?? [], raw.grouped_lines ?? []).map(invoiceLineToRenditionLine)
}

/** The subset of `GET purchases/invoices/<id>/` the association reads. */
export interface RawInvoiceDetail {
  lines?: InvoiceLine[]
  grouped_lines?: GroupedInvoiceLine[]
}

export function hydrateRenditionDraft(raw: RawRenditionDetail, formData: RenditionFormData): RenditionDraftDetail {
  return {
    policyId: raw.policy.id,
    policyName: raw.policy.name,
    policyDisplayName: raw.policy.display_name,
    policyCurrencyCode: raw.default_currency,
    description: raw.description ?? '',
    categoryId: raw.category?.id,
    subcategoryId: raw.subcategory?.id,
    areaId: raw.area?.id,
    // Header cost center: the single header Split that carries one (the request-level selector's value).
    costCenterId: (raw.splits ?? []).find((s) => s.cost_center)?.cost_center?.id,
    ...(() => {
      const { fields, model } = hydrateFormResponses(raw.form_responses)
      return { formResponse: fields, formResponseModel: model }
    })(),
    // Beneficiary + the bank data saved WITH this request (the payload's `recipient_*` keys). Read back
    // from the request, not from the employee's ficha: the user may have typed an account for this
    // rendition only, and that is what must reappear when the draft is reopened.
    beneficiaryId: raw.recipient?.id,
    recipientBank: raw.recipient?.bank ?? undefined,
    recipientAccountType: raw.recipient?.account_type ?? undefined,
    recipientAccountNumber: raw.recipient?.account_number ?? undefined,
    recipientEmail: raw.recipient?.email ?? undefined,
    recipientTaxId: raw.recipient?.national_id ?? undefined,
    invoices: (raw.items ?? []).map((item) => mapInvoiceFromApi(item, formData)),
    summary: raw.summary ? mapSummary(raw.summary) : undefined,
  }
}
